/**
 * The model's confidence, graded against what you actually observed.
 *
 * The README's first honest limit says the quiet part outright: *confidence
 * scores are the model's own, calibrated by instruction, not by measurement.*
 * They are also the loudest thing on the canvas — confidence sets strand
 * thickness — so the most prominent channel on screen has, until now, been the
 * least accountable number in the system.
 *
 * This measures it. Every ingredient was already on disk: `confidenceBefore` is
 * written once when the first result lands and never touched by an edit, the
 * bearing says what happened, and the node's `ModelStamp` says whose rating it
 * was. Joining those three is arithmetic — there is no model call in this file
 * and there must never be one, for the same reason `panel.ts` counts agreement
 * instead of asking for it. A model asked to grade its own calibration would be
 * producing exactly the kind of self-report this exists to replace.
 *
 * What it can and cannot claim is the whole design:
 *
 * - **Per model, never pooled.** Self-ratings do not compare across models — the
 *   sidebar, the inspector and both exports already say so — so a figure mixing
 *   two of them would be an average over two different scales.
 * - **Inconclusive results are counted and never scored.** A test that settled
 *   nothing is not evidence the model was right or wrong, and silently dropping
 *   those records would shrink the denominator without saying so.
 * - **Below `MIN_SCORED` there is no score at all.** Four results are an
 *   anecdote. The counts still show, because "you have tested three claims" is
 *   itself worth reading — and `scoredShortfall` says how many more are wanted,
 *   so an accruing table reads as a meter filling rather than as a dead feature.
 * - **It is your sample, not a random one.** People test the claims they doubt,
 *   or the ones that are cheap to test. That is a real bias, it is not
 *   correctable here, and every surface that prints these numbers says so.
 *
 * TWO INPUTS, ONE ARITHMETIC. Recorded results are the strongest evidence this app
 * holds and the rarest: five of them means five trips to a lab, so for almost every
 * session the ledger above is empty and the most distinctive honesty mechanism in the
 * product is dormant. Source-check readings are the other thing that enters from
 * outside a reasoning pass — the same facts `/until 3 refuted` is allowed to end a
 * paid run on, while `/until novelty` is refused — so a reading is scored against a
 * confidence too, in a table kept strictly beside the first and never merged into it.
 * See `CalibrationRow` for what is shared and `CalibrationAgainst` for what is not.
 *
 * The objection that this is the model grading itself does not land, and it is worth
 * being precise about why: the READING is gated on retrieved sources, and the citation
 * gate deletes anything the search did not actually return. That is the same reason a
 * reading is already trusted to terminate a run that costs money.
 */

import type { ModelStamp } from './types'

/**
 * One rating and what later answered it.
 *
 * Structural rather than a named record type, because there are now two kinds of thing
 * that answer a rating and the arithmetic must not fork: `OutcomeRecord` is a result
 * somebody observed, `CheckRecord` is a reading a source check returned, and both
 * satisfy this. Sharing `calibrate` is what keeps the two tables comparable — two
 * copies of a Brier score is how one of them comes to be computed differently.
 *
 * What must NOT be shared is the tables themselves. A recorded result outranks a
 * reading, and averaging the two would produce one figure that answers neither
 * question. Every caller keeps them apart, and every surface labels which it is
 * showing.
 */
export interface CalibrationRow {
  sessionId: string
  /** The model that produced the claim, and therefore the rating being graded. */
  model: ModelStamp
  /** What it claimed BEFORE anything answered it. */
  confidence: number
  bearing: 'supported' | 'refuted' | 'inconclusive'
  recordedAt: number
}

/**
 * What kind of evidence a table is scored against, for the one-line reading.
 *
 * The distinction is load-bearing rather than cosmetic: "tested" means somebody
 * observed something, "checked" means a search came back and its citations survived
 * reconciliation. Calling the second one tested would be the single most misleading
 * word this app could print.
 */
export type CalibrationAgainst = 'tested' | 'checked'

/**
 * Results below which a score is withheld.
 *
 * Not a confidence interval — with this few records the honest move is to print
 * the fraction and let a reader judge it, not to attach a number that would be
 * read as a measurement.
 */
export const MIN_SCORED = 5

/**
 * How much daylight the skill clause needs, in standard errors, before it commits.
 *
 * At an `n` barely past `MIN_SCORED`, a raw `brier < baseline` comparison flips on
 * a single result: one more claim lands, the mean of six squared errors moves by a
 * sixth of the spread, and the sentence somebody read yesterday says the opposite
 * today. One standard error is the smallest margin that stops the sentence
 * oscillating.
 *
 * It is emphatically NOT a confidence interval and is never printed as one — see
 * `MIN_SCORED`, which withholds a score rather than attach a number that would be
 * read as a measurement. This gates a SENTENCE and nothing else: inside the margin
 * the verdict says it cannot tell on this sample, which is that same refusal in
 * words rather than a second number to misread.
 */
export const VERDICT_MARGIN_SE = 1

/** One slice of the confidence range, and what happened inside it. */
export interface Band {
  lo: number
  hi: number
  scored: number
  supported: number
  /** Mean rating the model gave in this band — what it claimed. */
  claimed: number
}

export interface ModelCalibration {
  /** The most recently seen stamp for this model; `key` is what groups them. */
  model: ModelStamp
  key: string
  explorations: number
  /** Supported plus refuted. The denominator of every score below. */
  scored: number
  supported: number
  refuted: number
  /** Recorded, and deliberately outside every number above. */
  inconclusive: number
  /**
   * Mean squared error between the rating and what happened, 0 to 1, lower
   * better. Null below `MIN_SCORED`.
   */
  brier: number | null
  /**
   * How far `brier` itself would move on another sample this size: the standard
   * error of the mean of the per-row squared errors, `sd/√n`. Null with `brier`.
   *
   * Exists for `verdictOf` and for nothing else. It is never rendered, never
   * compared against another model's, and never dressed up as an interval — a
   * spread is a fact about how few rows there are, and printing it beside a Brier
   * score would hand a reader a second number to mistake for a measurement.
   */
  brierSe: number | null
  /**
   * What a rating that ignored the claim entirely and always said the observed
   * base rate would have scored on THIS sample — `p(1−p)`.
   *
   * Printed beside `brier` always, because a Brier score alone is unreadable: a
   * 0.21 is good against a base rate of 0.5 and worthless against one of 0.9.
   * Beating this is the only sense in which the ratings carried information.
   */
  baseline: number | null
  /**
   * Mean rating minus the fraction that held up. Positive is overconfident.
   *
   * The one number here a person can act on directly, and the reason it is
   * reported next to its two components rather than alone.
   */
  drift: number | null
  bands: Band[]
}

const BAND_EDGES = [0, 0.2, 0.4, 0.6, 0.8, 1.0001] as const

/** Groups a stamp. Provider and id, never the display name, which changes. */
export const modelKey = (m: ModelStamp): string => `${m.provider}:${m.id}`

const mean = (xs: number[]): number => xs.reduce((a, b) => a + b, 0) / xs.length
const round = (x: number, places = 3): number => Math.round(x * 10 ** places) / 10 ** places

/**
 * Standard error of the mean of `xs`, on the sample standard deviation.
 *
 * `n − 1`, not `n`: these rows are the results somebody happened to record, and
 * the population divisor would under-state the spread on exactly the small
 * samples this margin exists for. Never called below `MIN_SCORED`, so the
 * denominator is at least four.
 */
function stderr(xs: number[]): number {
  const m = mean(xs)
  const variance = xs.reduce((a, x) => a + (x - m) ** 2, 0) / (xs.length - 1)
  return Math.sqrt(variance / xs.length)
}

/**
 * One table per model that has had a claim of its tested.
 *
 * Ordered by how much has been tested, descending: the model you have actually
 * measured belongs above the one with two records against it.
 */
export function calibrate(records: readonly CalibrationRow[]): ModelCalibration[] {
  const groups = new Map<string, CalibrationRow[]>()
  for (const r of records) {
    const k = modelKey(r.model)
    const g = groups.get(k)
    if (g) g.push(r)
    else groups.set(k, [r])
  }

  const out: ModelCalibration[] = []
  for (const [key, rs] of groups) {
    // `supported` and `refuted` only. An inconclusive result is a result, and
    // it is counted below — but it cannot say the rating was too high or too
    // low, so admitting it here would be inventing an answer for it.
    const scored = rs.filter((r) => r.bearing !== 'inconclusive')
    const supported = scored.filter((r) => r.bearing === 'supported').length
    const enough = scored.length >= MIN_SCORED

    const rate = scored.length ? supported / scored.length : 0
    // Built once and used twice. Two passes over the same rows is how a mean and
    // the spread around it come to be computed from different arithmetic.
    const errors = scored.map((r) => (r.confidence - (r.bearing === 'supported' ? 1 : 0)) ** 2)
    const brier = enough ? mean(errors) : null

    const bands: Band[] = []
    for (let i = 0; i < BAND_EDGES.length - 1; i++) {
      const lo = BAND_EDGES[i]!
      const hi = BAND_EDGES[i + 1]!
      const inBand = scored.filter((r) => r.confidence >= lo && r.confidence < hi)
      if (!inBand.length) continue
      bands.push({
        lo,
        hi: Math.min(hi, 1),
        scored: inBand.length,
        supported: inBand.filter((r) => r.bearing === 'supported').length,
        claimed: round(mean(inBand.map((r) => r.confidence))),
      })
    }

    // The newest stamp wins the display name: a model keeps its id across a
    // rename, and the ledger should print what it is called now.
    const newest = rs.reduce((a, b) => (b.recordedAt > a.recordedAt ? b : a))

    out.push({
      model: newest.model,
      key,
      explorations: new Set(rs.map((r) => r.sessionId)).size,
      scored: scored.length,
      supported,
      refuted: scored.length - supported,
      inconclusive: rs.length - scored.length,
      brier: brier === null ? null : round(brier),
      brierSe: enough ? round(stderr(errors)) : null,
      baseline: enough ? round(rate * (1 - rate)) : null,
      drift: enough ? round(mean(scored.map((r) => r.confidence)) - rate) : null,
      bands,
    })
  }

  return out.sort((a, b) => b.scored - a.scored || b.inconclusive - a.inconclusive)
}

/**
 * How far short of a score this table is, or null once it has one.
 *
 * The counterpart to `verdictOf` and it occupies the same line: where a scored
 * model gets its one-line reading, an accruing one gets the arithmetic of its own
 * accrual. Without it the under-floor state prints counts and then stops, which
 * reads as a feature that does not work — and the ledger is structurally in that
 * state for almost every session, because five recorded results means five trips
 * to a lab. Progress and absence look identical until somebody prints the
 * remainder.
 *
 * It states the two numbers and no third one. "Claims" rather than "results",
 * because the same sentence serves the readings table, and calling a search
 * result a result is the one word this app must not blur.
 */
export function scoredShortfall(c: ModelCalibration): string | null {
  if (c.scored >= MIN_SCORED) return null
  const short = MIN_SCORED - c.scored
  return `${c.scored} scored claim${c.scored === 1 ? '' : 's'} — ${short} more before this table says anything.`
}

/**
 * The one-line reading, or null when there is not enough to say one.
 *
 * Deliberately returns null rather than hedged prose: a sentence like "slightly
 * overconfident, but it is early" reads as a finding at a glance no matter how
 * it is qualified, and the surfaces that show this already print the counts.
 */
export function verdictOf(
  c: ModelCalibration,
  against: CalibrationAgainst = 'tested',
): string | null {
  if (c.brier === null || c.drift === null || c.baseline === null || c.brierSe === null) return null
  const dir = c.drift > 0 ? 'above' : 'below'
  const size = Math.abs(c.drift)
  // 0.1 is a tenth of the scale — under it, the ratings and the results are
  // closer together than the granularity anyone assigns confidence at.
  const shape = size < 0.1 ? 'tracked what happened' : `sat ${round(size, 2)} ${dir} what happened`
  // Three states, not two. The comparison is `brier` against `baseline` either
  // way; what the margin buys is a middle where the gap is smaller than the
  // sample's own wobble, and the sentence says so instead of picking a side it
  // would swap on the next result. Both edges are strict, so a dead tie — which
  // is what a flat rating scoring exactly its own baseline is — lands in the
  // middle rather than counting as a loss.
  const margin = VERDICT_MARGIN_SE * c.brierSe
  const skill =
    c.brier + margin < c.baseline
      ? 'and carried information a flat rating would not have'
      : c.brier - margin > c.baseline
        ? 'and did not beat a flat rating on this sample'
        : // "landed" rather than a second "sat": the shape clause above already
          // uses that verb, and one sentence carrying it twice reads as a slip.
          'and landed too close to a flat rating to call on this sample'
  return `Across ${c.scored} ${against} claim${c.scored === 1 ? '' : 's'}, its confidence ${shape} ${skill}.`
}

/**
 * What one recorded result counts toward, for the line that points from the
 * result to the ledger.
 *
 * The calibration table is the one place this app grades the model rather than
 * quoting it, and nothing pointed at it from the one press that feeds it:
 * somebody recorded a result, read "Result recorded", and never learned it was
 * the first of five a score is waiting for. So the pointer is READ OFF THE TABLE
 * — `calibrate`'s own row for the claim's model, and `MIN_SCORED` — rather than
 * counted a second way here: the sentence under the result and the row in the
 * sheet it leads to cannot disagree about the number, because they are one
 * number. The table keeps printing its own remainder (`scoredShortfall`); this
 * says where one result stands in it.
 *
 * A result the ledger never reads says why instead of pointing at nothing: the
 * worked example's authored result, a claim with no model's rating on it, and a
 * claim the person rewrote — the same three exclusions `loadOutcomeRecords`
 * applies, asked of the node rather than inferred from the table's silence.
 */
export type ResultPointer =
  | { kind: 'toward'; model: string; scored: number; of: number; settled: boolean }
  | { kind: 'scored'; model: string; scored: number; settled: boolean }
  | { kind: 'none'; why: 'demo' | 'unrated' | 'rewritten' }

/** The node fields the three exclusions read — structural, so the store's node
 *  goes straight in and this module keeps importing types only. */
export interface PointedNode {
  model?: ModelStamp
  origin?: string
  editedAt?: number
  outcome?: { bearing: CalibrationRow['bearing'] }
}

/** Why a result on this claim scores no model, or null where it would score its
 *  model — the three exclusions, asked of the node. One copy for the pointer
 *  after a result and the line before one, so the card never promises a count
 *  the ledger then declines to keep. */
function scoresNone(
  node: PointedNode,
  demo: boolean,
): { kind: 'none'; why: 'demo' | 'unrated' | 'rewritten' } | null {
  if (demo) return { kind: 'none', why: 'demo' }
  if (!node.model) return { kind: 'none', why: 'unrated' }
  if (node.origin === 'manual' || node.editedAt !== undefined)
    return { kind: 'none', why: 'rewritten' }
  return null
}

export function resultPointer(
  ledger: readonly ModelCalibration[],
  node: PointedNode,
  demo: boolean,
): ResultPointer | null {
  if (!node.outcome) return null
  const none = scoresNone(node, demo)
  if (none) return none
  const key = modelKey(node.model!)
  const row = ledger.find((c) => c.key === key)
  // Read from the shelf plus the open exploration, so the row is there by the
  // time this is asked; a missing one is a table built before the result, and
  // saying nothing beats a count of zero the sheet would contradict.
  if (!row) return null
  const settled = node.outcome.bearing !== 'inconclusive'
  return row.scored >= MIN_SCORED
    ? { kind: 'scored', model: row.model.name, scored: row.scored, settled }
    : { kind: 'toward', model: row.model.name, scored: row.scored, of: MIN_SCORED, settled }
}

/**
 * What a result on this claim WOULD count toward, before one is recorded — the
 * pointer above, asked of the press that feeds it rather than of its result.
 *
 * D197 pointed from a recorded result to the score it counts toward, which means
 * a reader learned what recording builds toward only after recording, which is
 * the one moment the fact no longer changes anything. So the card's Record line
 * says it first, from the same table and the same `MIN_SCORED`: "1 of 5" where
 * the pointer after the result will say "1 of 5", because a result that settles
 * the claim is one more scored row than the table holds now. A result that
 * settles nothing is counted and not scored, so the line says what it says of
 * one that settles it — the form's own words ("Did not settle it").
 *
 * Null wherever a result would score no model — the worked example, a claim with
 * no model's rating on it, one the reader rewrote — and wherever a result is
 * already on the claim, whose own pointer speaks. The card says nothing then: a
 * line explaining why nothing would count is a line about a table the reader was
 * not reaching for. No row for the model is a table with nothing scored yet.
 */
export function pendingPointer(
  ledger: readonly ModelCalibration[],
  node: PointedNode,
  demo: boolean,
): Extract<ResultPointer, { kind: 'toward' | 'scored' }> | null {
  if (node.outcome || scoresNone(node, demo)) return null
  const key = modelKey(node.model!)
  const row = ledger.find((c) => c.key === key)
  const model = row?.model.name ?? node.model!.name
  const scored = row?.scored ?? 0
  return scored >= MIN_SCORED
    ? { kind: 'scored', model, scored, settled: true }
    : { kind: 'toward', model, scored: scored + 1, of: MIN_SCORED, settled: true }
}

/** The pending pointer as the sentence the Record line ends on. */
export function pendingPointerLine(p: NonNullable<ReturnType<typeof pendingPointer>>): string {
  return p.kind === 'toward'
    ? `One that settles it would be ${p.scored} of ${p.of} toward scoring ${p.model}’s confidence.`
    : `One that settles it would join the ${p.scored} that score ${p.model}’s confidence.`
}

/** Said first when the result settled nothing, so "1 of 5" is never read as
 *  counting it — the table's own rule, in the table's own words. */
const UNSETTLED = 'it settled nothing, so it is counted, not scored · '

/** The pointer as one lower-case clause, for "Result recorded · …". */
export function resultPointerLine(p: ResultPointer): string {
  switch (p.kind) {
    case 'none':
      return p.why === 'demo'
        ? 'part of the worked example, so it scores no model'
        : p.why === 'unrated'
          ? 'no model’s rating is on this claim, so it scores none'
          : 'you rewrote this claim, so its rating is yours and scores no model'
    case 'toward':
      return `${p.settled ? '' : UNSETTLED}${p.scored} of ${p.of} toward scoring ${p.model}’s confidence`
    case 'scored':
      return `${p.settled ? '' : UNSETTLED}${p.model}’s confidence is scored on ${p.scored} result${p.scored === 1 ? '' : 's'}`
  }
}
